import * as React from 'react'
import { connect } from 'react-redux'
import * as Exp from '../../../../experimentConstants'

import Button from '@doordash/component-button'
import {
  ErrorMessage,
  IdentificationField,
  Select,
  TextField,
} from '@doordash/component-fields'
import {
  Breakpoints,
  InlineChildren,
  Stack,
  StackChildren,
  Text,
} from '@doordash/design-language'
import { FeatureFlag, isFeatureFlagEnabled } from '@doordash/lib-feature-flags'
import { get, noop } from 'lodash'
import MediaQuery from 'react-responsive'
import { STATE_CODES } from '../../../../constants'
import { ValidatorList } from '../../../../models/types'
import Form from '../../../../utilities/Form'
import { formatDateWithLeadingZeroes } from '../../../../utilities/formatDate'
import {
  validateCheckrDriversLicenseByState,
  validators as sharedValidators,
} from '../../../../utilities/validators'
import { BirthdateInputSplit } from '../../../atoms/birthdate-input'
import { SsnDescription, SsnField, SsnLabel, SsnLabelWrapper } from './styles'

import * as DA from '../../../../typeModules/events'
import validatorStrings from '../../../../utilities/validatorStrings'
import t from '../strings'

import { BackgroundCheck as Tag } from '../../../../models/selectorAnchors'
import { getValidator } from '../../../../selectors/identificationStep'
import { formatErrorKeys } from '../../../../utilities/Form/utils'
import { VGSForm } from '../../../../utilities/hooks'

type Props = {
  uniqueLink?: string
  error?: string
  onSubmit?: any
  needsLicenseInfo: boolean
  vgsForm: VGSForm
  isSsnValid: boolean
  isVgsLoaded: boolean
  experiments: object
  trackBackgroundCheckSteps: (data: DA.TrackBackgroundCheck) => Promise<any>
}

type State = {
  hasSsnError: boolean
}

class IdentificationStep extends React.Component<Props, State> {
  public static fields = {
    ssn: '',
    confirmSsn: '',
    birthday: '',
    licenseNumber: '',
    licenseState: '',
  }

  public static noLicenseFields = {
    ssn: '',
    confirmSsn: '',
    birthday: '',
  }

  public state = {
    hasSsnError: false,
  }

  public static validators: ValidatorList = {
    ssn: sharedValidators.ssn,
    confirmSsn: noop,
    birthday: sharedValidators.birthday,
    licenseNumber: sharedValidators.licenseNumber,
    licenseState: sharedValidators.licenseState,
  }

  public static noLicenseValidators: ValidatorList = {
    ssn: sharedValidators.ssn,
    confirmSsn: noop,
    birthday: sharedValidators.birthday,
  }

  public static checkrValidators: ValidatorList = {
    ssn: sharedValidators.ssn,
    confirmSsn: noop,
    birthday: sharedValidators.birthday,
    licenseNumber: validateCheckrDriversLicenseByState,
    licenseState: sharedValidators.licenseState,
  }

  public componentDidMount() {
    this.props.trackBackgroundCheckSteps({
      page: DA.BackgroundCheckPages.BackgroundCheckDL,
      action: DA.EventActions.Rendered,
    })
  }

  public handleSubmit(data) {
    // Use ssn validation passed through vgs if tokenization is enabled
    if (isFeatureFlagEnabled(Exp.TOKENIZATION) && !this.props.isSsnValid) {
      this.setState({ hasSsnError: true })
      return
    }

    // Format birthday before submission
    try {
      const processedData = Object.assign(data, {
        birthday: formatDateWithLeadingZeroes(get(data, 'birthday', null)),
      })

      return this.props.onSubmit(processedData)
    } catch (e) {
      console.error(e)
    }

    return this.props.onSubmit(data)
  }

  public render() {
    const { needsLicenseInfo, experiments } = this.props
    const validators = getValidator({
      needsLicenseInfo,
      experiments,
    })
    const fields = needsLicenseInfo
      ? IdentificationStep.fields
      : IdentificationStep.noLicenseFields

    if (isFeatureFlagEnabled(Exp.TOKENIZATION)) {
      delete validators.ssn
      delete validators.confirmSsn
      delete fields.ssn
      delete fields.confirmSsn
    }

    return (
      <React.Fragment>
        <Stack size={Stack.Sizes.Small}>
          <Text styles={Text.Styles.PageTitle}>{t.title()}</Text>
        </Stack>
        <StackChildren>
          <Text styles={Text.Styles.PageDescriptionBody}>
            {t.backgroundCheckDescriptionNew()}
          </Text>

          <Form
            onSubmit={data => this.handleSubmit(data)}
            validators={validators}
            fields={fields}
          >
            {(fields, handleChange, handleSubmit, { isLoading }) => (
              <form onSubmit={handleSubmit}>
                <StackChildren size={StackChildren.Sizes.Large}>
                  {needsLicenseInfo && (
                    <React.Fragment>
                      <MediaQuery minWidth={Breakpoints.Desktop + 1}>
                        <InlineChildren>
                          <div
                            data-anchor-id={Tag.LicenseState}
                            style={{ width: '100%' }}
                          >
                            <Select
                              accessibilityDescribedBy={'licenseState'}
                              label={t.licenseStateLabel()}
                              description={t.required()}
                              disabledValue={t.licenseStateDisabled()}
                              onChange={handleChange.bind(this, 'licenseState')}
                              options={STATE_CODES}
                              {...fields.licenseState}
                            />
                          </div>
                          <TextField
                            accessibilityDescribedBy={'licenseNumber'}
                            label={t.licenseNumberLabel()}
                            description={t.required()}
                            onChange={handleChange.bind(this, 'licenseNumber')}
                            autoComplete="off"
                            data-anchor-id={Tag.LicenseNumber}
                            {...fields.licenseNumber}
                          />
                        </InlineChildren>
                      </MediaQuery>
                      <MediaQuery maxWidth={Breakpoints.Desktop}>
                        <div data-anchor-id={Tag.LicenseState}>
                          <Select
                            accessibilityDescribedBy={'licenseState'}
                            label={t.licenseStateLabel()}
                            description={t.required()}
                            disabledValue={t.licenseStateDisabled()}
                            onChange={handleChange.bind(this, 'licenseState')}
                            options={STATE_CODES}
                            data-anchor-id={Tag.LicenseState}
                            {...fields.licenseState}
                          />
                        </div>
                        <TextField
                          accessibilityDescribedBy={'licenseNumber'}
                          label={t.licenseNumberLabel()}
                          description={t.required()}
                          onChange={handleChange.bind(this, 'licenseNumber')}
                          autoComplete="off"
                          data-anchor-id={Tag.LicenseNumber}
                          {...fields.licenseNumber}
                        />
                      </MediaQuery>
                    </React.Fragment>
                  )}
                  <BirthdateInputSplit
                    id="birthdate-input-split"
                    label={t.birthdate()}
                    onChange={handleChange.bind(this, 'birthday')}
                    birthdayField={fields.birthday}
                    data-anchor-id={Tag.BirthdateContainer}
                    monthAnchorId={Tag.BirthdateMonth}
                    dayAnchorId={Tag.BirthdateDay}
                    yearAnchorId={Tag.BirthdateYear}
                    {...fields.birthday}
                  />
                  <FeatureFlag
                    flagName={Exp.TOKENIZATION}
                    on={() => (
                      <div>
                        <SsnLabelWrapper>
                          <SsnLabel htmlFor="ssn">
                            {t.socialSecurityNumber()}
                          </SsnLabel>
                          <SsnDescription>{t.required()}</SsnDescription>
                        </SsnLabelWrapper>
                        {this.state.hasSsnError && (
                          <ErrorMessage error={validatorStrings.ssnInvalid()} />
                        )}
                        <SsnField id="ssn" />
                      </div>
                    )}
                    off={() => (
                      <React.Fragment>
                        <IdentificationField
                          country="US"
                          accessibilityDescribedBy={'socialSecurityNumber'}
                          onChange={handleChange.bind(this, 'ssn')}
                          label={t.socialSecurityNumber()}
                          description={t.required()}
                          autoComplete="off"
                          data-anchor-id={Tag.PersonalIdentificationNumber}
                          {...fields.ssn}
                        />
                        <IdentificationField
                          country="US"
                          accessibilityDescribedBy={
                            'confirmSocialSecurityNumber'
                          }
                          onChange={handleChange.bind(this, 'confirmSsn')}
                          label={t.confirmSocialSecurityNumber()}
                          description={t.required()}
                          autoComplete="off"
                          {...fields.confirmSsn}
                        />
                      </React.Fragment>
                    )}
                  />
                  <Button
                    accessibilityDescribedBy={'submitBackgroundCheck'}
                    onClick={async () => {
                      this.props.trackBackgroundCheckSteps({
                        page: DA.BackgroundCheckPages.BackgroundCheckDL,
                        action: DA.EventActions.Continue,
                      })
                      try {
                        await handleSubmit()
                      } catch (e) {
                        this.props.trackBackgroundCheckSteps({
                          page: DA.BackgroundCheckPages.BackgroundCheckDL,
                          action: DA.EventActions.Error,
                          value: formatErrorKeys(e, fields, true),
                        })
                      }
                    }}
                    state={
                      isLoading ? Button.States.Loading : Button.States.Default
                    }
                    data-anchor-id={Tag.PrimaryAction}
                  >
                    {t.continue()}
                  </Button>
                </StackChildren>
              </form>
            )}
          </Form>
        </StackChildren>
      </React.Fragment>
    )
  }
}

const mapStateToProps = state => ({
  experiments: state.experiments,
})

export default connect(mapStateToProps)(IdentificationStep)
